/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { withNamespaces } from 'react-i18next';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import Lottie from 'react-lottie';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import Swiper from 'react-id-swiper';
import SidePanelEnquiryButton from '../SidePanelEnquiryButton';
import SidePanel from '../../SolutionBuilder/SidePanel';
import FeaturesAndPricingPanel from '../FeaturesAndPricingPanel';
import FeaturesAndPricingPanelFooter from '../FeaturesAndPricingPanelFooter';
import { Container, Row, Column } from '../../../../styles/Grid';
import * as dicts from '../../../../utils/dictionarykeys';
import applyPluralRule from '../../../../utils/pluralRule';
import { getPeopleMultiplier } from '../../../../utils/getPeopleMultiplier';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import searchAnimation from '../../../../assets/animations/search-load-grey.json';
import { formatPriceToCurrencyString } from '../../../ui/PriceGenerator';
import Locations from '../Locations';
import * as styles from './index.styles.js';

const defaultData = {
  Context: {
    relatedLocation: '',
    Geolocations: [],
  },
  Copy: {
    value: '',
  },
  Match: [],
  AlternateTag: { value: '' },
  Tag: { value: '' },
};

const animationOptions = {
  loop: true,
  autoplay: true,
  animationData: searchAnimation,
};

class Recommendation extends SafePureComponent {
  fields = defaultsDeep(this.props.fields, defaultData);

  state = {
    isSidePanelOpen: false,
  };

  renderAnswerItem(label, id, icon, peopleValueParam) {
    if (!label) return null;

    const { peopleMultiplier } = this.props;
    const peopleCount =
      peopleValueParam in peopleMultiplier
        ? getPeopleMultiplier(peopleValueParam, peopleMultiplier)
        : '';
    const peopleLabel = peopleCount
      ? applyPluralRule({
          quantity: peopleCount,
          dictionary: dicts.SOLUTION_BUILDER.PEOPLE,
        })
      : '';
    const getPeopleValue = () => {
      return (
        <span>
          {' '}
          {peopleCount} {peopleLabel}{' '}
        </span>
      );
    };

    return (
      <div
        className="swiper-slide"
        css={[styles.answer, icon && styles.answerWithIcon]}
        key={`answer-${id}`}
      >
        {icon && <i css={styles.answerIcon} className={`icon-${icon}`} />}
        {label}
        {/* only the location has an icon, solutions don't. This is for lounge that does not has a people value param */}
        {!icon && getPeopleValue()}
      </div>
    );
  }

  openSidePanelHandler = () => {
    this.setState({ isSidePanelOpen: true });
  };

  closeSidePanelHandler = () => {
    this.setState({ isSidePanelOpen: false });
  };

  renderPricingSidePanelBody = () => {
    const { detailedPrices, fields } = this.props;
    const { Copy, Features } = fields;
    const data = {
      copy: Copy,
      features: Features,
      services: detailedPrices,
    };

    return <FeaturesAndPricingPanel {...data} />;
  };

  renderPricingSidePanelFooter = () => {
    const { detailedPrices, locationsPrices } = this.props;
    const priceUnits = detailedPrices.length > 0 ? detailedPrices[0] : {};
    const total = get(locationsPrices, 'min.Price', '');
    const data = {
      currency: priceUnits.currency,
      total,
    };

    return <FeaturesAndPricingPanelFooter {...data} />;
  };

  renderTags = () => {
    const { AlternateTag, isAlternateMatch, Tag } = this.fields;

    if (Boolean(isAlternateMatch)) {
      return (
        <React.Fragment>
          <li css={[styles.tag, styles.tagAlternate]}>
            {this.props.t(dicts.SOLUTION_BUILDER.ALTERNATIVE_RECOMMENDATION)}
          </li>
          <li css={styles.tag}>{AlternateTag.value}</li>
        </React.Fragment>
      );
    } else {
      return <li css={styles.tag}>{Tag.value}</li>;
    }
  };

  safeRender() {
    const { fields, props } = this;
    const { t, loading } = props;
    const { detailedPrices, locationsPrices } = this.props;
    const total = get(locationsPrices, 'min.Price', '');
    const priceUnits = detailedPrices.length ? detailedPrices[0] : {};
    const firstLocation = get(props.locationsPrices, 'min', {});

    if (fields.Match.length < 1) {
      return null;
    }

    if (loading) {
      return (
        <Container
          css={[
            styles.recommendation,
            fields.isAlternateMatch && styles.recommendationAlternative,
          ]}
        >
          <Lottie
            options={animationOptions}
            isClickToPauseDisabled
            height={60}
          />
        </Container>
      );
    }

    return (
      <Container
        css={[
          styles.recommendation,
          fields.isAlternateMatch && styles.recommendationAlternative,
        ]}
      >
        <Row>
          <Column
            cols={{ sm: 6, md: 8, lg: 6, xl: 7 }}
            css={styles.summaryWrapper}
          >
            <div css={styles.summary}>
              <ul css={styles.tags}>{this.renderTags()}</ul>
              <p>
                <Text field={fields.Copy}></Text>
              </p>
              <Swiper freeMode={true} slidesPerView={'auto'} spaceBetween={8}>
                {this.renderAnswerItem(
                  fields.Geolocations,
                  'geolocations',
                  'location',
                )}
                {fields.Match.map(({ fields }, index) => {
                  const label = get(fields, 'Text.value', '');
                  const peopleValueParam = get(
                    fields,
                    'PeopleValueParam.value',
                    '',
                  );

                  return this.renderAnswerItem(
                    label,
                    index,
                    null,
                    peopleValueParam,
                  );
                })}
              </Swiper>
              <p css={styles.price}>
                {t(dicts.COMMONS.FROM)} {t(priceUnits.currency)}{' '}
                {total && formatPriceToCurrencyString(total)}{' '}
                <span css={styles.priceMessage}>
                  /{t(dicts.SOLUTION_BUILDER.MONTHTEXT)}.*
                </span>
              </p>
            </div>
            <div css={styles.stickyBar}>
              <SidePanel
                body={this.renderPricingSidePanelBody}
                footer={this.renderPricingSidePanelFooter}
                isSidePanelOpen={this.state.isSidePanelOpen}
                onRequestClose={this.closeSidePanelHandler}
                toggleSidePanel={this.closeSidePanelHandler}
              />
              <SidePanelEnquiryButton location={firstLocation} />
              <button
                css={styles.pricesCta}
                onClick={this.openSidePanelHandler}
              >
                {t(dicts.SOLUTION_BUILDER.FEATURES_AND_PRICING)}
              </button>
            </div>
          </Column>
          <Column cols={{ sm: 6, md: 7, lg: 5, xl: 4 }}>
            <div css={styles.locations}>
              <Locations
                fields={fields}
                locationsPrices={props.locationsPrices}
                currency={priceUnits.currency}
              />
            </div>
          </Column>
        </Row>
      </Container>
    );
  }
}

const objectValueType = {
  value: PropTypes.string,
};

Recommendation.displayName = 'SolutionBuilderResults/Recommendation';

Recommendation.defaultProps = {
  fields: defaultData,
  t: key => key,
};

const stringPropShape = {
  value: PropTypes.string,
};

Recommendation.propTypes = {
  fields: PropTypes.shape({
    Context: PropTypes.shape({
      Geolocations: PropTypes.arrayOf(PropTypes.string),
      relatedLocation: PropTypes.string,
    }),
    Copy: PropTypes.shape(objectValueType),
    AlternateTag: PropTypes.shape(stringPropShape),
    Tag: PropTypes.shape(stringPropShape),
    Features: PropTypes.arrayOf(PropTypes.shape({})),
    Match: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          Text: PropTypes.shape(objectValueType),
        }),
      }),
    ),
  }),
  locations: PropTypes.any,
  loading: PropTypes.bool,
  t: PropTypes.func,
};

export default withNamespaces()(Recommendation);
